import {
  DayOfWeek,
  DestinationResponse,
  DestinationRow,
  TimeZoneOption,
} from './communicationPreferences.types';

/** Locked 6-zone allow-list (no Arizona). Central is the default. */
export const TIMEZONE_OPTIONS: TimeZoneOption[] = [
  { label: 'Eastern (ET)', ianaId: 'America/New_York' },
  { label: 'Central (CT)', ianaId: 'America/Chicago' },
  { label: 'Mountain (MT)', ianaId: 'America/Denver' },
  { label: 'Pacific (PT)', ianaId: 'America/Los_Angeles' },
  { label: 'Alaska (AKT)', ianaId: 'America/Anchorage' },
  { label: 'Hawaii (HT)', ianaId: 'Pacific/Honolulu' },
];

export const DEFAULT_TIME_ZONE = 'America/Chicago';

export const timeZoneLabel = (ianaId: string): string =>
  TIMEZONE_OPTIONS.find((tz) => tz.ianaId === ianaId)?.label ?? 'Central (CT)';

export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const dayLabel = (day: number): string =>
  DAY_LABELS[day] ?? DAY_LABELS[DayOfWeek.Thursday];

/** Formats an hour (0–23) as a 12-hour on-the-hour label, e.g. 9 → "9:00 AM". */
export const formatHour = (hour: number): string => {
  const normalized = ((hour % 24) + 24) % 24;
  const period = normalized < 12 ? 'AM' : 'PM';
  const twelve = normalized % 12 === 0 ? 12 : normalized % 12;
  return `${twelve}:00 ${period}`;
};

/**
 * DS `Dropdown` treats `label` as an `<optgroup>` grouping key and shows
 * `displayValue ?? value` as the option text. Use `displayValue` for the
 * human-readable text so the list stays flat and never surfaces the raw value.
 */
export interface SelectOption {
  value: string;
  id: string;
  displayValue: string;
}

export const DAY_OPTIONS: SelectOption[] = DAY_LABELS.map((label, value) => ({
  value: String(value),
  id: `day-${value}`,
  displayValue: label,
}));

export const HOUR_OPTIONS: SelectOption[] = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  id: `hour-${hour}`,
  displayValue: formatHour(hour),
}));

export const TIMEZONE_SELECT_OPTIONS: SelectOption[] = TIMEZONE_OPTIONS.map((tz) => ({
  value: tz.ianaId,
  id: tz.ianaId,
  displayValue: tz.label,
}));

export const activeDestinationCount = (rows: DestinationRow[]): number =>
  rows.filter((row) => row.isActive && row.emailAddress.trim().length > 0).length;

/** Maps the API's destination list to the rows we render (active rows only). */
export const toDestinationRows = (
  destinations: DestinationResponse[],
): DestinationRow[] =>
  destinations
    .filter((destination) => destination.isActive)
    .map((destination) => ({
      key: `existing-${destination.emailSettingId}`,
      emailSettingId: destination.emailSettingId,
      emailAddress: destination.emailAddress,
      isActive: destination.isActive,
    }));
